import type { HttpClient } from '~/shared/api'
import { ApiService, toBodyModel, toModel } from '~/shared/api'
import type { Logger } from '~/shared/interfaces'
import type { QueryParams } from '~/shared/interfaces/params'
import { DigitalLectureCourseDetail, DigitalLectureCourseListResult } from '~/models/digital-lecture'
import { API_PREFIX } from '~/shared/const/request-services'

export class DigitalLectureCoursesService extends ApiService {
  constructor(
    http: HttpClient,
    private readonly logger: Logger
  ) {
    super(http)
  }

  readonly urls = {
    list: `${API_PREFIX}/digital-lecture/dl-courses`,
    bySlug: (slug: string) => `${API_PREFIX}/digital-lecture/dl-courses/${slug}`,
    like: (slug: string) => `${API_PREFIX}/digital-lecture/dl-courses/${slug}/like`,
    pageView: (slug: string) => `${API_PREFIX}/digital-lecture/dl-courses/${slug}/page-view`,
  }

  getCoursesList(params: QueryParams = {}): Promise<DigitalLectureCourseListResult> {
    return this.http
      .request({
        url: this.urls.list,
        params: { sort: '-date_publication', 'page[size]': 15, ...params },
      })
      .then(toBodyModel(DigitalLectureCourseListResult))
  }

  getBySlug(slug: string): Promise<DigitalLectureCourseDetail> {
    return this.http
      .request({ url: this.urls.bySlug(slug), silent: true })
      .then(toModel(DigitalLectureCourseDetail))
  }

  async addLike(slug: string): Promise<boolean> {
    try {
      const res = await this.http.request<{ data?: { success?: boolean } }>({
        method: 'POST',
        url: this.urls.like(slug),
        silent: true,
      })

      return Boolean(res.data?.data?.success)
    } catch (error) {
      this.logger.warn(`Failed to like course "${slug}"`, error)
      return false
    }
  }

  async addPageView(slug: string): Promise<boolean> {
    try {
      await this.http.request({ method: 'POST', url: this.urls.pageView(slug), silent: true })
      return true
    } catch (error) {
      this.logger.warn(`Failed to count page-view for course "${slug}"`, error)
      return false
    }
  }
}
